import Box from '@mui/material/Box';

import {
  AppImage,
  introSlideshowKeyframesSx,
  SectionPattern,
  slideMotion,
} from '@/components/atoms';
import { type PageIntroSlide } from '@/hooks/usePageIntroSlides';

const REDUCE = '@media (prefers-reduced-motion: reduce)';

/**
 * The light on the stage: a key off the upper right, where the cars stand, and
 * a pool on the floor beneath them.
 *
 * Without it the band is one flat value of black and a grey car loses its
 * roofline and its wheel arches into it — which is the same failure the home
 * hero hit from the other direction, where dark cars vanished on its stage.
 */
const KEY_LIGHT = [
  'radial-gradient(58% 72% at 74% 38%, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 68%)',
  'radial-gradient(ellipse 32% 12% at 72% 84%, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 72%)',
].join(',');

/**
 * The scrim, layered rather than flat.
 *
 * A single even wash protects the one corner the copy sits in by dulling the
 * whole picture, which costs the picture to save the words. These darken where
 * they are needed and nowhere else: heavily at the left, where the headline
 * is, tailing off to almost nothing at the right, where the car is; and again
 * from the bottom, so the buttons have a floor.
 *
 * Every alpha here came down when the band stopped being a mid dusty rose and
 * became the near-black stage the rest of the site's inverse surfaces use. The
 * old figures were doing two jobs at once: holding the car back from the
 * headline, *and* dragging a mid-tone surface down far enough for white type
 * to sit on it. Only the first job is left. A scrim still set for the second
 * one does real damage on a stage — at 0.94 it buries `aurora` under the two
 * thirds of the band the copy occupies, and the band comes back as the flat
 * slab the mesh was painted to prevent. The headline loses nothing: white on
 * near-black clears AA several times over before the scrim is applied at all.
 *
 * The stops themselves are unchanged, and they are the part that is not
 * negotiable — they moved right when the copy column widened to 58–62%, so
 * that the end of the headline is still over scrim rather than over a wash
 * that has begun to lift.
 *
 * A phone has no left and right to speak of — the copy is full width and the
 * car is behind all of it — so there the wash *is* even, and heavier.
 */
const SCRIM = '--kabex-intro-scrim';

const SCRIM_MD = [
  `linear-gradient(90deg, rgba(var(${SCRIM}) / 0.80) 0%, rgba(var(${SCRIM}) / 0.72) 50%, rgba(var(${SCRIM}) / 0.40) 72%, rgba(var(${SCRIM}) / 0.04) 100%)`,
  `linear-gradient(0deg, rgba(var(${SCRIM}) / 0.34) 0%, rgba(var(${SCRIM}) / 0) 38%)`,
].join(',');

/**
 * Catalogue media, looping behind the opening copy of a page.
 *
 * Decorative in full: `aria-hidden`, every image `alt=""`, and nothing in here
 * can be reached by a pointer. It is the backdrop the page is set against, not
 * content — the headline says what the page is, and a screen reader that had
 * to sit through image labels before reaching it would be worse off.
 *
 * Four layers, bottom to top: the theme's `aurora` mesh, the key light, the
 * media, and the scrim over all of them. The media goes under the scrim and not
 * over it, because the whole point of a scrim is that it is between the
 * picture and the words.
 *
 * Catalogue headers keep a small image at every width. The product cards still
 * begin directly below the header, and the smaller mobile media leaves the
 * copy readable while giving both catalogues a distinct, useful visual cue.
 *
 * Fade and drift are two elements and not one. A single element cannot be
 * given two animations whose `transform` and `opacity` restart together
 * without them sharing a timeline they do not agree on, and splitting them
 * also keeps the car's own dimming — which is static — off the property the
 * loop is animating.
 */
export function PageIntroSlideshow({ slides }: { slides: readonly PageIntroSlide[] }) {
  return (
    <Box
      aria-hidden
      sx={{
        display: 'block',
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        ...introSlideshowKeyframesSx(slides.length),
      }}
    >
      <SectionPattern name="aurora" />
      <Box sx={{ position: 'absolute', inset: 0, background: KEY_LIGHT }} />

      {slides.map((slide, i) => {
        const motion = slideMotion(i, slides.length);
        return (
          <Box
            key={slide.name}
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              px: { xs: '1%', md: '2%', lg: '3%' },
              opacity: motion.restingOpacity,
              animation: motion.fade,
              [REDUCE]: { animation: 'none' },
            }}
          >
            <Box
              sx={{
                width: slide.kind === 'photo' ? { xs: '36%', md: '32%' } : { xs: '44%', md: '39%' },
                maxWidth: Math.min(slide.width, slide.kind === 'photo' ? 320 : 360),
                ...(slide.kind === 'photo'
                  ? {
                      height: { xs: 100, sm: 120, md: 150 },
                    }
                  : {
                      filter: 'drop-shadow(0 18px 22px rgba(0,0,0,0.55))',
                    }),
                opacity: slide.kind === 'photo' ? 0.96 : 0.88,
                animation: motion.drift,
                [REDUCE]: { animation: 'none', transform: 'none' },
              }}
            >
              {/* These are decorative images, so they load lazily without
                  taking high fetch priority away from the page's content. */}
              <AppImage
                picture={slide.image}
                alt=""
                sizes={slide.sizes}
                style={{
                  width: '100%',
                  height: slide.kind === 'photo' ? '100%' : 'auto',
                  display: 'block',
                  objectFit: 'contain',
                }}
              />
            </Box>
          </Box>
        );
      })}

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: {
            xs: [
              'linear-gradient(90deg, rgba(var(--kabex-intro-scrim) / 0.88) 0%, rgba(var(--kabex-intro-scrim) / 0.70) 48%, rgba(var(--kabex-intro-scrim) / 0.24) 100%)',
              'linear-gradient(0deg, rgba(var(--kabex-intro-scrim) / 0.58) 0%, rgba(var(--kabex-intro-scrim) / 0) 58%)',
            ].join(','),
            md: SCRIM_MD,
          },
        }}
      />
    </Box>
  );
}
